import React, { ComponentType } from 'react';
import { Heading04, BodyCopy } from '@uds/typography';
import PaddingBox from '@uds/padding-box';
import Illustration from '@uds/illustrations';
import { useTheme, gu2px, Colorway } from '@uds/theme-utils';
import { useBreakpoints } from '@uds/responsive';
import { Size } from '@uds/button';
import { Alignment, ImagesWithAspectRatios } from 'core/ribbons/primitives';
import { CTA } from 'core/ribbons/cta';
import { ImageAspectRatio } from 'domain/constants/imageAspectRatio';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import createImageAlt from 'presentation/utils/createImageAlt';
import urlLanguageParse from 'presentation/utils/urlLanguageParse';
import withLanguage from 'presentation/hocs/withLanguage';
import getActiveImageWithAspectRatios from 'presentation/utils/getActiveImageWithAspectRatios';
import CallToAction from 'presentation/components/common/CallToAction';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';

import useStylesheet from './Pillar.stylesheet';

interface OuterProps {
  hBaseline?: number;
  preserveDimension?: string;
  colorway: Colorway;
  body?: ShortcodeProps;
  title?: ShortcodeProps;
  illustration?: string;
  imageUrl?: string;
  alignment: Alignment;
  imageAspectRatio?: ImageAspectRatio;
  imagesWithAspectRatios?: ImagesWithAspectRatios;
  ctas?: Array<CTA>;
  ribbonId: string;
}

export interface InnerProps extends OuterProps {
  currentLanguage: string;
}

export const PurePillar = ({
  hBaseline,
  illustration,
  imageUrl,
  title,
  body,
  colorway,
  alignment,
  currentLanguage,
  imageAspectRatio,
  preserveDimension = 'width',
  imagesWithAspectRatios,
  ctas,
  ribbonId,
}: InnerProps) => {
  const { theme } = useTheme();
  const breakpoints = useBreakpoints();
  const { isMinSm } = breakpoints;
  const activeImage = getActiveImageWithAspectRatios(
    breakpoints,
    {
      imageUrl,
      imageAspectRatio,
      preserveDimension,
    },
    imagesWithAspectRatios,
  );
  const hasIllustration = !isNilOrEmpty(illustration);
  const hasImageUrl = !isNilOrEmpty(activeImage.url);
  const hasImage = hasIllustration || hasImageUrl;
  const hasTitle = !isNilOrEmpty(title);
  const hasBody = !isNilOrEmpty(body);

  const { styles } = useStylesheet({
    theme,
    preserveDimension: activeImage.preserveDimension,
    alignment,
    hasTitle,
    hasBody,
    imageAspectRatio: activeImage.imageAspectRatio,
    isMinSm,
    ctas,
  });

  return (
    <PaddingBox>
      <div style={styles.root}>
        {hasImage && (
          <div style={styles.imageContainer}>
            {hasIllustration && (
              <Illustration size={styles.illustration.width as number} icon={illustration as string} />
            )}
            {hasImageUrl && (
              <>
                <div style={styles.imageBuffer} />
                <div style={styles.imageInner}>
                  <img
                    loading="lazy"
                    src={urlLanguageParse(activeImage.url ?? '', currentLanguage)}
                    alt={createImageAlt({ imageUrl: activeImage.url })}
                    height={gu2px(16, theme.spacing)}
                    style={styles.image}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {hasTitle && (
          <Heading04 depth={hBaseline}>
            <Shortcode code={title} against={colorway} />
          </Heading04>
        )}
        {hasBody && (
          <BodyCopy>
            <Shortcode code={body} against={colorway} />
          </BodyCopy>
        )}
        {ctas == null ? null : (
          <div style={styles.ctas}>
            {ctas.map((cta, i) => (
              <div style={styles.cta} key={(ribbonId ?? '').concat(String(i))}>
                <CallToAction block={!isMinSm} ribbonId={ribbonId} index={i} size={Size.SM} {...cta} />
              </div>
            ))}
          </div>
        )}
      </div>
    </PaddingBox>
  );
};

PurePillar.displayName = 'Pillar';

export default withLanguage(PurePillar) as ComponentType<OuterProps>;
